import React, {Fragment} from 'react';
import {LogoBox} from "../user-menu/logo-box/logo-box.component";
import {observer} from 'mobx-react';
import {Button} from '../../basic-ui-components/button/base/button.component';
import {OrbitComponent} from '../base-components/orbit-component/orbit-component';
import {GameSearchComponent} from '../game-search/game-search.component';
import {HeaderConfiguration} from '../../services/configurations/config-data-sections/header.configuration';
import {HelpButtonComponent} from './top-header-right/help-button/help-button.component';
import {CrossBrandsComponent} from './top-header-left/cross-brand/cross-brands.component';
import {RegulatoryDataComponent} from "./top-header-left/regulatory-data/regulatory-data.component";
import {MobileBurgerMenuComponent} from './top-header-left/mobile-burger-menu/mobile-burger-menu.component';
import {HeaderWelcomeButtonsComponent} from './welcome-buttons/header-welcome-buttons.component';
import {HeaderLanguageSwitcherComponent} from './top-header-right/language-switcher/header-language-switcher.component';
import {InsideOrbitMenuContainerComponent} from "../inside-orbit-menu-container/inside-orbit-menu-container.component";
import {DepositHeaderButtonComponent} from "./top-header-right/deposit/deposit-header-button.component";
import {NavbarSeparatorBox} from "./boxes/navbar-separator/navbar-separator.box";
import {HeaderNavbarContainerBox} from "./boxes/header-navbar-container/header-navbar-container.box";
import {HeaderNavbarMobileWrapperBox} from "./boxes/header-navbar-mobile-wrapper/header-navbar-mobile-wrapper.box";
import {HeaderNavbarLeftContainerBox} from "./boxes/navbar-left-container/header-navbar-left-container.box";
import {HeaderNavbarRightContainerBox} from "./boxes/navbar-right-container/header-navbar-right-container.box";
import {Image} from "../raw-components/image/image";
import {StateSelectorButtonComponent} from "./top-header-right/state-selector-button/state-selector-button.component";
import {AreaType} from '@sparkware/uc-sdk-core';
import {CHANNEL_SOURCE, CHANNEL_ELEMENTS} from '../../../common/const/casinofied-analytics-consts';
import {OPEN_SAFEZONE_SOURCE_VALUES} from '../../services/safezone/safezone.service.interface';
import {CyClass} from '../../../common/cy-classes';


@observer
export class HeaderComponent extends OrbitComponent {

    private get _headerConfig(): HeaderConfiguration {
        return this.services.configuration.data.header;
    }

    private _captureHeaderRef = (element: HTMLElement | null) => {
        this.services.header.setHeaderElement(element || null);
    };

    private _renderInsideOrbitMenuContainerPC() {
        return <InsideOrbitMenuContainerComponent/>;
    }

    private _renderDesktopSearchInput() {

        if (this._headerConfig?.header_buttons?.search?.display !== true ) {
            return null;
        }

        return <GameSearchComponent/>;
    }

    private _renderDesktopSafezoneButton() {

        if (!this.services.safezone.isEnabled) {
            return null;
        }

        return (
            <Button
                preset="flatStroked"
                icon={{Component: this.services.theme.currentTheme.icons.safeZoneIcon}}
                onClick={() => {
                    this.services.userMenu.closeMenu();
                    this.services.appLauncher.openControlCenterFull({
                        businessCorrelationID: this.services.tools.getGuid(),
                        channel: {
                            area: AreaType.Header,
                            element: CHANNEL_ELEMENTS.safezoneHeaderButton,
                            source: CHANNEL_SOURCE.casinoClient
                        },
                        appSpecificParameters: {
                            source: OPEN_SAFEZONE_SOURCE_VALUES.LOBBY
                        }
                    });
                }}
                margin="0 0.4rem"
                tooltip={this.services.translation.translate('SAFE_GAMBLING_TOOLTIP')}
                className={CyClass("cy-header-control-center-button")}
            />
        );
    }

    private _renderDesktopHelpButton() {
        if (this._headerConfig?.header_buttons?.help?.display !== true ) {
            return null;
        }

        return <HelpButtonComponent/>;
    }

    private _renderDesktopStateSelectorButton() {

        if (!this.services.stateOfResidenceIdentifier.shouldDisplayStateSelectorButton()) {
            return null;
        }

        return <StateSelectorButtonComponent/>;
    }

    private _renderDesktopLanguageSwitcher() {
        if (this._headerConfig?.header_buttons?.languages?.display !== true || this.services.language.languageSwitcher.isEmpty ) {
            return null;
        }

        return <HeaderLanguageSwitcherComponent/>;
    }

    private _renderMobileNavbar() {
        return (
            <HeaderNavbarMobileWrapperBox ref={this._captureHeaderRef} className="cy-navbar-mobile-wrapper">
                <RegulatoryDataComponent/>
                <HeaderNavbarContainerBox className="cy-navbar-container">
                    <HeaderNavbarLeftContainerBox className="cy-navbar-left-container">
                        <MobileBurgerMenuComponent/>
                        <NavbarSeparatorBox className="cy-navbar-separator"/>
                        <LogoBox withAnalytics={true}/>
                    </HeaderNavbarLeftContainerBox>
                    <HeaderNavbarRightContainerBox className="cy-navbar-right-container">
                        { this._renderDeposit() }
                        <HeaderWelcomeButtonsComponent/>
                    </HeaderNavbarRightContainerBox>
                </HeaderNavbarContainerBox>
            </HeaderNavbarMobileWrapperBox>
        );
    }

    private _renderSecondLogo() {
        if(!this._headerConfig?.secondaryLogoUrl) {
            return null;
        }

        return (
            <Image src={this._headerConfig?.secondaryLogoUrl}/>
        );
    }

    private _renderRelevantComponent(componentIDs: string[]) {
        let elements: Array<{component: React.ReactElement | null; componentID: string}> = [];
        if(!componentIDs) {
            return null;
        }

        for(let index= 0; index < componentIDs.length; index++) {
            const currentComponentID = componentIDs[index];
            switch(currentComponentID) {
                case "app-language-switcher":
                    elements.push({
                        component: this._renderDesktopLanguageSwitcher(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-help-button":
                    elements.push({
                        component: this._renderDesktopHelpButton(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-safezone":
                    elements.push({
                        component: this._renderDesktopSafezoneButton(),
                        componentID: currentComponentID
                    });
                    break;
                case "state-selection-button":
                    elements.push({
                        component: this._renderDesktopStateSelectorButton(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-responsible-icons":
                    elements.push({
                        component: this._renderResponsibleIcons(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-secondLogo":
                    elements.push({
                        component: this._renderSecondLogo(),
                        componentID: currentComponentID
                    });
                    break;
                case "search-box-form":
                    elements.push({
                        component: this._renderDesktopSearchInput(),
                        componentID: currentComponentID
                    });
                    break;
                case "state-cross-brand":
                    elements.push({
                        component: this._renderCrossBrand(),
                        componentID: currentComponentID
                    });
                    break;
            }
        }

        elements = elements.filter(function (el) {
            return el.component != null;
        });

        if(elements.length === 0) {
            return null;
        }

        return (
            elements.map((element, index)=> {
                return (
                    <Fragment key={element.componentID}>
                        {element.component}
                        { index !== (elements.length - 1) && <NavbarSeparatorBox/>}
                    </Fragment>
                )
            })
        );
    }

    private _renderResponsibleIcons() {
        if(!this._headerConfig?.responsibleIcons?.display === true) {
            return null;
        }
        return <RegulatoryDataComponent/>;
    }

    private _renderDesktopLeftNavbar() {
        return (
            <HeaderNavbarLeftContainerBox className="cy-navbar-left-container">
                {this._renderRelevantComponent(this._headerConfig?.top_header_left)}
            </HeaderNavbarLeftContainerBox>
        );
    }

    private _renderCrossBrand() {
        if(!this._headerConfig?.cross_brand?.display === true) {
            return null;
        }
        return (
            <CrossBrandsComponent/>
        );
    }

    private _renderDeposit() {
        if (!this._headerConfig?.header_buttons?.deposit === true) {
            return null;
        }
        if (!this.services.user.isAuthorized) {
            return null;
        }
        return <DepositHeaderButtonComponent />;
    }

    private _renderDesktopRightNavbar() {
        return (
            <HeaderNavbarRightContainerBox className="cy-navbar-right-container">
                {this._renderInsideOrbitMenuContainerPC()}
                {this._renderRelevantComponent(this._headerConfig?.top_header_right)}

                <HeaderWelcomeButtonsComponent/>
            </HeaderNavbarRightContainerBox>
        );
    }

    render() {

        if (this.services.device.isMobile) {
            return this._renderMobileNavbar();
        }

        return (
            <HeaderNavbarContainerBox ref={this._captureHeaderRef} className="cy-navbar-container">
                {this._renderDesktopLeftNavbar()}
                {this._renderDesktopRightNavbar()}
            </HeaderNavbarContainerBox>
        );
    }
}
